import { useEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, GoneError } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import { PageShell } from "@/components/PageShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Skeleton } from "@/components/Skeleton";

/**
 * Per user-flows.md §5:
 *   - 410 Gone   → `/t/<slug>/deleted`
 *   - 404 → try `getCurrentSlug`; redirect to live slug, or
 *           `/t/<slug>/gone` when nothing rescues it.
 *   - 200 → render <Outlet /> with the trip in cache.
 */
export function TripLoader() {
  const { slug = "" } = useParams<{ slug: string }>();

  const trip = useQuery({
    queryKey: queryKeys.trip(slug),
    queryFn: () => api.getTrip(slug),
    enabled: slug.length > 0,
    retry: false,
  });

  const rescue = useQuery({
    queryKey: ["current-slug", slug],
    queryFn: () => api.getCurrentSlug(slug),
    enabled:
      slug.length > 0 &&
      trip.isError &&
      trip.error instanceof ApiError &&
      trip.error.status === 404,
    retry: false,
  });

  // Cheap effect: warm React Query cache for the new slug if rescue succeeded.
  useEffect(() => {
    if (rescue.data && rescue.data.slug !== slug) {
      // The redirect happens via <Navigate> below; nothing imperative to do.
    }
  }, [rescue.data, slug]);

  if (trip.isLoading) {
    return (
      <PageShell header={<BrandHeader />}>
        <Skeleton variant="block" width="100%" height={120} />
      </PageShell>
    );
  }

  if (trip.error instanceof GoneError) {
    return <Navigate to={`/t/${slug}/deleted`} replace />;
  }

  if (trip.error instanceof ApiError && trip.error.status === 404) {
    if (rescue.isLoading) {
      return (
        <PageShell header={<BrandHeader />}>
          <Skeleton variant="block" width="100%" height={80} />
        </PageShell>
      );
    }
    if (rescue.data && rescue.data.slug && rescue.data.slug !== slug) {
      return <Navigate to={`/t/${rescue.data.slug}`} replace />;
    }
    return <Navigate to={`/t/${slug}/gone`} replace />;
  }

  if (trip.isError) {
    return (
      <PageShell header={<BrandHeader />}>
        <p>Не вдалося завантажити поїздку. Спробуй пізніше.</p>
      </PageShell>
    );
  }

  return <Outlet />;
}
